import React, { useEffect, useState } from "react";
import NewCustomerModal from "./NewCustomerModal";

export interface Customer {
  id: number;
  name: string;
  phone: string;
  email: string;
  address: string;
  orgNumber: string;
}

export interface EquipmentAddon {
  name: string;
  price: number;
}

export interface Equipment {
  name: string;
  addons?: EquipmentAddon[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newBooking: any) => void;
  customers: Customer[];
  onAddCustomer: (newCustomer: Customer) => void;
}

const dummyEquipment: Equipment[] = [
  { name: "Minigraver 2.5t" },
  {
    name: "Kjernebor maskin",
    addons: [
      { name: "Bor 51mm", price: 200 },
      { name: "Bor 62mm", price: 250 },
    ],
  },
];

const deliveryOptions = [
  "Tur/Retur",
  "Kunden henter og leverer",
  "Vi leverer",
  "Vi henter",
];

const NewBookingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  customers,
  onAddCustomer,
}) => {
  const [customerInput, setCustomerInput] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const [customerDetails, setCustomerDetails] = useState({
    phone: "",
    email: "",
    address: "",
    orgNumber: "",
  });

  const [bestiller, setBestiller] = useState("");
  const [label, setLabel] = useState("");
  const [equipment, setEquipment] = useState("");
  const [addons, setAddons] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState(deliveryOptions[0]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setStartDate(new Date().toISOString().slice(0, 10));
  }, []);

  useEffect(() => {
    if (!showDropdown) {
      setFilteredCustomers([]);
      return;
    }

    if (customerInput.trim()) {
      const filtered = customers.filter((c) =>
        c.name.toLowerCase().includes(customerInput.toLowerCase())
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers([]);
    }
  }, [customerInput, customers, showDropdown]);

  const selectedEquipment = dummyEquipment.find((eq) => eq.name === equipment);

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerInput(customer.name);
    setFilteredCustomers([]);
    setCustomerDetails({
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      orgNumber: customer.orgNumber,
    });
    setShowDropdown(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const title = `${equipment} - ${customerInput}`;
    onSave({
      title,
      start: new Date(startDate),
      end: endDate ? new Date(endDate) : new Date(startDate),
      allDay: true,
      customer: customerInput,
      ...customerDetails,
      bestiller,
      label,
      equipment,
      addons,
      deliveryAddress,
      deliveryMethod,
      notes,
    });

    resetForm();
    onClose();
  };

  const resetForm = () => {
    setCustomerInput("");
    setSelectedCustomer(null);
    setCustomerDetails({
      phone: "",
      email: "",
      address: "",
      orgNumber: "",
    });
    setBestiller("");
    setLabel("");
    setEquipment("");
    setAddons([]);
    setStartDate(new Date().toISOString().slice(0, 10));
    setEndDate("");
    setDeliveryAddress("");
    setDeliveryMethod(deliveryOptions[0]);
    setNotes("");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 overflow-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-white p-8 rounded w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4">Ny utleie</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Kunde-felt */}
          <div className="flex gap-4 items-end">
            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-gray-700">
                Kunde
              </label>
              <input
                type="text"
                value={customerInput}
                onChange={(e) => {
                  setCustomerInput(e.target.value);
                  setSelectedCustomer(null);
                  setShowDropdown(true);
                }}
                className="mt-1 block w-full border-gray-300 rounded shadow-sm focus:ring focus:ring-blue-200"
                placeholder="Søk eller skriv kunde"
                required
              />
              {showDropdown && filteredCustomers.length > 0 && (
                <ul className="absolute bg-white border rounded mt-1 w-full z-10 max-h-40 overflow-auto">
                  {filteredCustomers.map((c) => (
                    <li
                      key={c.id}
                      onClick={() => handleSelectCustomer(c)}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {c.name} ({c.phone})
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowNewCustomerModal(true)}
              className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white rounded"
            >
              Ny kunde
            </button>
          </div>

          {/* Autofylte felter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Telefon"
              value={customerDetails.phone}
              onChange={(v) =>
                setCustomerDetails({ ...customerDetails, phone: v })
              }
            />
            <InputField
              label="E-post"
              value={customerDetails.email}
              onChange={(v) =>
                setCustomerDetails({ ...customerDetails, email: v })
              }
            />
            <InputField
              label="Adresse"
              value={customerDetails.address}
              onChange={(v) =>
                setCustomerDetails({ ...customerDetails, address: v })
              }
            />
            <InputField
              label="Org.nr"
              value={customerDetails.orgNumber}
              onChange={(v) =>
                setCustomerDetails({ ...customerDetails, orgNumber: v })
              }
            />
          </div>

          <InputField
            label="Bestillers navn"
            value={bestiller}
            onChange={setBestiller}
          />

          <InputField
            label="Merking på faktura"
            value={label}
            onChange={setLabel}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Utstyr
            </label>
            <select
              value={equipment}
              onChange={(e) => {
                setEquipment(e.target.value);
                setAddons([]);
              }}
              className="mt-1 block w-full border-gray-300 rounded shadow-sm focus:ring focus:ring-blue-200 text-lg py-3"
              required
            >
              <option value="">Velg utstyr</option>
              {dummyEquipment.map((eq) => (
                <option key={eq.name} value={eq.name}>
                  {eq.name}
                </option>
              ))}
            </select>
          </div>

          {selectedEquipment?.addons && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tilleggsutstyr
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {selectedEquipment.addons.map((addon) => (
                  <label
                    key={addon.name}
                    className="flex items-center space-x-2"
                  >
                    <input
                      type="checkbox"
                      checked={addons.includes(addon.name)}
                      onChange={() => {
                        if (addons.includes(addon.name)) {
                          setAddons(addons.filter((a) => a !== addon.name));
                        } else {
                          setAddons([...addons, addon.name]);
                        }
                      }}
                      className="border-gray-300 rounded"
                    />
                    <span className="text-sm">
                      {addon.name} ({addon.price} kr)
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Leveringsmåte
            </label>
            <div className="flex gap-2 flex-wrap">
              {deliveryOptions.map((option) => (
                <button
                  type="button"
                  key={option}
                  onClick={() => setDeliveryMethod(option)}
                  className={`px-3 py-2 rounded ${
                    deliveryMethod === option
                      ? "bg-green-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <InputField
            label="Leveringsadresse"
            value={deliveryAddress}
            onChange={setDeliveryAddress}
          />

          <div className="flex gap-4">
            <DateField
              label="Startdato"
              value={startDate}
              onChange={setStartDate}
              required
            />
            <DateField
              label="Sluttdato"
              value={endDate}
              onChange={setEndDate}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notater
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded shadow-sm focus:ring focus:ring-blue-200"
              rows={3}
              placeholder="Evt. spesielle detaljer som skal stå på fakturaen"
            ></textarea>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-800"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
            >
              Lagre
            </button>
          </div>
        </form>

        <NewCustomerModal
          isOpen={showNewCustomerModal}
          onClose={() => setShowNewCustomerModal(false)}
          onSave={(newCustomer) => {
            onAddCustomer({
              id: customers.length + 1,
              ...newCustomer,
            });
            handleSelectCustomer({
              id: customers.length + 1,
              ...newCustomer,
            });
            setShowNewCustomerModal(false);
          }}
        />
      </div>
    </div>
  );
};

// Gjenbrukbare komponenter

const InputField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 block w-full border-gray-300 rounded shadow-sm focus:ring focus:ring-blue-200"
    />
  </div>
);

const DateField = ({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  required?: boolean;
}) => (
  <div className="flex-1">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 block w-full border-gray-300 rounded shadow-sm focus:ring focus:ring-blue-200"
      required={required}
    />
  </div>
);

export default NewBookingModal;
